import React, { memo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  AdminPanelSettings,
  Person,
} from '@mui/icons-material';
import { getInitials, formatCelular } from '../../utils/userUtils';
import { canEdit, canDelete } from '../../utils/permissionUtils';
import { AVAILABLE_PERMISSIONS, USER_ROLES, THEME_COLORS } from '../../constants';

/**
 * Componente de tarjeta de usuario para vista móvil
 */
const UserCard = memo(({
  user,
  isExpanded,
  onExpand,
  onEdit,
  onDelete,
  currentUser,
  showActions = true,
}) => {
  const isAdmin = user.rol === USER_ROLES.ADMIN;
  const canEditUser = canEdit(currentUser, 'users');
  const canDeleteUser = canDelete(currentUser, 'users');

  return (
    <Card
      sx={{
        marginBottom: 2,
        border: `1px solid ${THEME_COLORS.BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: THEME_COLORS.WHITE,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: THEME_COLORS.BORDER_HOVER,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        {/* Header del usuario */}
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: isAdmin ? THEME_COLORS.PRIMARY : THEME_COLORS.SECONDARY,
              marginRight: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            {getInitials(user.nombre)}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: THEME_COLORS.TEXT_PRIMARY,
                marginBottom: 0.5,
              }}
            >
              {user.nombre}
            </Typography>
            <Chip
              icon={isAdmin ? <AdminPanelSettings /> : <Person />}
              label={user.rol}
              size="small"
              sx={{
                backgroundColor: isAdmin ? THEME_COLORS.PRIMARY : THEME_COLORS.LIGHT_GRAY,
                color: isAdmin ? THEME_COLORS.WHITE : THEME_COLORS.TEXT_SECONDARY,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            />
          </Box>
          
          {showActions && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEditUser && (
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: THEME_COLORS.SECONDARY,
                    '&:hover': {
                      backgroundColor: THEME_COLORS.LIGHT_GRAY,
                      color: THEME_COLORS.PRIMARY,
                    },
                  }}
                  onClick={() => onEdit(user)}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
              {canDeleteUser && (
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: THEME_COLORS.SECONDARY,
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      color: '#d32f2f',
                    },
                  }}
                  onClick={() => onDelete(user)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Información de contacto */}
        <Box sx={{ marginBottom: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: THEME_COLORS.TEXT_SECONDARY, 
              marginBottom: 0.5,
              fontSize: '0.875rem',
            }}
          >
            {user.correo}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: THEME_COLORS.TEXT_SECONDARY,
              fontSize: '0.875rem',
            }}
          >
            {formatCelular(user.celular)}
          </Typography>
        </Box>

        {/* Botón de permisos */}
        <Button
          fullWidth
          variant="outlined"
          onClick={() => onExpand(user.user_id)}
          endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
          sx={{
            borderColor: THEME_COLORS.BORDER_LIGHT,
            color: THEME_COLORS.TEXT_SECONDARY,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: THEME_COLORS.PRIMARY,
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
              color: THEME_COLORS.PRIMARY,
            },
          }}
        >
          {isExpanded ? 'Ocultar Permisos' : 'Ver Permisos'}
        </Button>

        {/* Permisos expandibles */}
        <Collapse in={isExpanded}>
          <Box sx={{ marginTop: 2, paddingTop: 2, borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}` }}>
            <Typography
              variant="subtitle2"
              sx={{ 
                marginBottom: 1.5, 
                fontWeight: 600, 
                color: THEME_COLORS.TEXT_PRIMARY,
                fontSize: '0.9rem',
              }}
            >
              Permisos del Usuario
            </Typography>
            <FormGroup>
              {AVAILABLE_PERMISSIONS.map((permiso) => (
                <FormControlLabel
                  key={permiso.key}
                  control={
                    <Checkbox
                      checked={user.permisos?.[permiso.key] || false}
                      disabled
                      size="small"
                      sx={{
                        color: THEME_COLORS.SECONDARY,
                        '&.Mui-checked': {
                          color: THEME_COLORS.PRIMARY,
                        },
                        '&.Mui-disabled': {
                          opacity: 0.6,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.825rem',
                        color: THEME_COLORS.TEXT_SECONDARY,
                      }}
                    >
                      {permiso.label}
                    </Typography>
                  }
                  sx={{ marginBottom: 0.5 }}
                />
              ))}
            </FormGroup>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
});

UserCard.displayName = 'UserCard';

export default UserCard; 